<!-- @format -->

<template>
	<el-tooltip content="列设置" placement="top">
		<span style="float: right">
			<el-popover placement="bottom-end" :width="200" trigger="click" popper-class="pc-table-column-setting">
				<template #reference>
					<slot></slot>
				</template>
				<div class="pc-table-column-setting-wrap">
					<draggable
						:list="columnList"
						item-key="code"
						v-bind="{
							sort: true,
							handle: '.pc-table-column-content-drag',
							ghostClass: 'ghost',
							animation: 300,
						}"
						@end="onDragEnd"
					>
						<template #item="{ element }">
							<div class="fields-item-content">
								<component :is="getSvg('drag')" class="el-icon pc-table-column-content-drag" />
								<el-checkbox
									v-model="checkList[element.code]"
									:label="element.label"
									class="pc-table-column-content-check"
									@change="onChecked"
								/>
							</div>
						</template>
					</draggable>
				</div>
			</el-popover>
		</span>
	</el-tooltip>
</template>

<script setup lang="ts">
	import { flatArr } from '@power/utils';
	import { draggable } from '@/components';
	import { getSvg } from '@/assets/icons/vue';
	import { getsysColumns, saveSysColumns } from '@/api/savecolumn';

	interface Props {
		autoSaveColumnProp?: {
			canAutoSave: boolean;
			module: string;
			page: string;
		};
		defaultColumn: string[];
		list: any[];
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emits = defineEmits(['changeColumnProp']);

	const loading = ref(false);

	const checkList = ref<any>({});

	const columnList = ref(flatArr(props.list || []));

	const checkItemList = computed(() => {
		return columnList.value.filter((itm) => {
			return checkList.value[itm.code];
		});
	});

	const getColumnsProp = async () => {
		loading.value = true;
		try {
			const res: any = await getsysColumns({
				module: props.autoSaveColumnProp?.module,
				page: props.autoSaveColumnProp?.page,
			});
			if (res) {
				checkList.value = (res || []).reduce((obj, item) => {
					obj[item] = true;
					return obj;
				}, {});

				updateOrderColumnsList(res);
				return res;
			}
		} catch (error) {
		} finally {
			loading.value = false;
		}
	};

	const updateOrderColumnsList = (columns) => {
		let sortedColumnList: any = [];
		// 创建一个 Set，用于快速判断一个 code 是否在 columns 中存在
		const columnsSet = new Set(columns);
		// 遍历 columnList，将存在的元素按照 columns 的顺序添加到 sortedColumnList
		const columnIndexMap = {};
		columns.forEach((code, index) => {
			if (columnList.value.some((item) => item.code === code)) {
				// 只考虑存在于 columnList 中的 codes
				columnIndexMap[code] = index;
			}
		});
		// 注意：由于我们之前可能添加了一些不在 columnList 中的 codes 到 columns，
		// 所以我们需要先过滤出那些真正存在于 columnList 中的 codes，然后再进行排序。
		// 但在这个例子中，为了简化，我们假设用户已经给出了正确的 columns（即只包含 columnList 中的 codes，或者愿意忽略那些不在 columnList 中的 codes）。
		// 如果要严谨处理，可以在这里添加一个过滤步骤。

		const inOrderElements: any = [];
		columnList.value.forEach((item) => {
			// eslint-disable-next-line no-prototype-builtins
			if (columnIndexMap.hasOwnProperty(item.code)) {
				inOrderElements.push({ ...item, sortIndex: columnIndexMap[item.code] }); // 添加一个临时属性用于排序
			}
		});

		inOrderElements.sort((a, b) => a.sortIndex - b.sortIndex); // 根据 sortIndex 排序

		inOrderElements.forEach((item) => {
			delete item.sortIndex; // 排序完成后删除临时属性
			sortedColumnList.push(item);
		});

		// 遍历 columnList，将不存在的元素添加到 sortedColumnList 的末尾
		const notInOrderElements = columnList.value.filter((item) => !columnsSet.has(item.code));
		sortedColumnList = sortedColumnList.concat(notInOrderElements);
		columnList.value = sortedColumnList;
	};

	const updateColumnProp = async () => {
		const columns = Object.entries(checkList.value).reduce(
			(arr: string[], [key, value]: [string, boolean]) => (value ? arr.concat(key) : arr),
			[],
		);

		const params = {
			columns: updateOrderColumns(columns),
			module: props.autoSaveColumnProp?.module,
			page: props.autoSaveColumnProp?.page,
		};
		await saveSysColumns(params);
	};

	const updateOrderColumns = (columns) => {
		const codeOrder = columnList.value.map((item) => item.code);
		// 用于存储排序后的在 codeOrder 中的元素和不在 codeOrder 中的元素
		const sortedInOrder: any = [];
		const notInOrder: any = [];
		// 遍历 columns，根据 codeOrder 对元素进行分类
		columns.forEach((col) => {
			if (codeOrder.includes(col)) {
				sortedInOrder.push(col);
			} else {
				notInOrder.push(col);
			}
		});
		// 对 sortedInOrder 进行排序，按照 codeOrder 的顺序
		sortedInOrder.sort((a, b) => codeOrder.indexOf(a) - codeOrder.indexOf(b));
		// 合并两个数组，得到最终的排序结果
		const sortedColumns = sortedInOrder.concat(notInOrder);
		return sortedColumns;
	};

	const onDragEnd = () => {
		if (props.autoSaveColumnProp?.canAutoSave) {
			updateColumnProp();
		}
	};

	const onChecked = async () => {
		emits('changeColumnProp');
		if (props.autoSaveColumnProp?.canAutoSave) {
			updateColumnProp();
		}
	};

	watch(
		[() => props.list, () => props.defaultColumn],
		async ([newVal1, newVal2]) => {
			columnList.value = newVal1;

			if (props.autoSaveColumnProp?.canAutoSave) {
				const res = await getColumnsProp();
				if (res) {
					return;
				}
			}

			const initValue = newVal1.reduce((obj: any, it) => {
				obj[it.code] = newVal2.includes(it.code);
				return obj;
			}, {});

			checkList.value = initValue;
		},
		{
			deep: true,
			immediate: true,
		},
	);

	defineExpose({ checkItemList, loading });
</script>

<style scoped lang="scss">
	.pc-table-column-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		border-bottom: 1px solid var(--el-border-color);
		padding: 0 8px;
	}

	.pc-table-column-setting-wrap {
		padding: 12px 18px;
		max-height: 400px;
		overflow-y: auto;

		.fields-item-content {
			display: flex;
			align-items: center;
			gap: 8px;
		}

		.pc-table-column-content-drag {
			cursor: move;
			width: 16px;
			height: 16px;
			color: var(--theme-text-color-disabled);
			flex-shrink: 0;
		}

		.pc-table-column-content-check {
			width: calc(100% - 24px);

			:deep(.el-checkbox__label) {
				width: calc(100% - 14px);
				text-overflow: ellipsis;
				overflow: hidden;
				word-break: break-all;
				white-space: nowrap;
			}
		}
	}
</style>
<style lang="scss">
	.pc-table-column-setting {
		padding: 0px !important;

		.ghost {
			outline: 1px solid var(--el-border-color);
			border-radius: 1px;
		}
	}
</style>
